'use client';

import {
  api_portalOfficeHospitalListing_responseFilter,
  api_portalOfficeHospitalListing_medicalSubject,
  api_portalOfficeHospitalListing_reportedAdditionalFee,
} from '@/init/swagger';
import { Box, Popover, SxProps } from '@mui/material';
import { SearchFilterButton } from './SearchFilterButton';
import { PopoverContentContainer } from './PopoverContentContainer';
import { usePopover } from './usePopover';
import { HOSPITAL_SEARCH_FILTER } from '@/types/types';
import { useLogic } from './logic';
import { Loading } from '@/components/loader/Loading';
import { RequireLoginButton } from './RequireLoginButton';

const containerStyle: SxProps = {
  display: 'flex',
  px: {
    sm: 5,
    lg: 15,
  },
  gap: 3,
  '@media (max-width: 800px)': {
    px: 2,
  },
};

const filterButtonContainerStyle: SxProps = {
  width: '100%',
  pt: 3,
  pb: 3,
  '@media (max-width: 800px)': {
    display: '',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
};

type Props = {
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  hospitalReportedAdditionalFees: api_portalOfficeHospitalListing_reportedAdditionalFee[];
  hospitalMedicalCares: api_portalOfficeHospitalListing_responseFilter[];
  hospitalAppealPoints: api_portalOfficeHospitalListing_responseFilter[];
  isPublicSearch?: boolean;
};

export function HospitalSearchFilters({
  hospitalMedicalSubjects,
  hospitalReportedAdditionalFees,
  hospitalMedicalCares,
  hospitalAppealPoints,
  isPublicSearch = false,
}: Props) {
  const {
    searchFilterEl,
    searchFilterType,
    openSearchFilter,
    closeSearchFilter,
  } = usePopover();

  const {
    watch,
    isPending,
    search,
    medicalSubjectSelectedFilterLabels,
    medicalCareSelectedFilterLabels,
    appealPointSelectedFilterLabels,
  } = useLogic({
    hospitalMedicalSubjects,
    hospitalReportedAdditionalFees,
    hospitalMedicalCares,
    hospitalAppealPoints,
    isPublicSearch,
  });

  return (
    <Box sx={containerStyle}>
      <Loading isLoading={isPending} />
      <Box sx={filterButtonContainerStyle}>
        <Box
          sx={{
            display: 'flex',
            gap: '8px',

            '@media (max-width: 740px)': {
              flexDirection: 'column',
            },
          }}
        >
          <SearchFilterButton
            text="診療科目・届出加算で絞る"
            selectedFiltersLabel={medicalSubjectSelectedFilterLabels}
            onClick={(e) => openSearchFilter(e, 'medicalSubject')}
            filterCount={
              (watch('medical_subjects')?.length || 0) +
              (watch('reported_additional_fees')?.length || 0)
            }
          />
          {isPublicSearch ? (
            <RequireLoginButton
              text="対応可能な施術で絞る"
              onClick={(e) => openSearchFilter(e, 'loginRequire')}
            />
          ) : (
            <SearchFilterButton
              text="対応可能な施術で絞る"
              selectedFiltersLabel={medicalCareSelectedFilterLabels}
              onClick={(e) => openSearchFilter(e, 'medicalCare')}
              filterCount={
                watch('filters')?.filter(
                  (v) =>
                    v.category_id ===
                    HOSPITAL_SEARCH_FILTER.MEDICAL_CARE_CATEGORY_ID,
                ).length || 0
              }
            />
          )}
          {isPublicSearch ? (
            <RequireLoginButton
              text="アピールポイントで絞る"
              onClick={(e) => openSearchFilter(e, 'loginRequire')}
            />
          ) : (
            <SearchFilterButton
              text="アピールポイントで絞る"
              selectedFiltersLabel={appealPointSelectedFilterLabels}
              onClick={(e) => openSearchFilter(e, 'appealPoint')}
              filterCount={
                watch('filters')?.filter(
                  (v) =>
                    v.category_id ===
                    HOSPITAL_SEARCH_FILTER.APPEAL_POINT_CATEGORY_ID,
                ).length || 0
              }
            />
          )}
        </Box>

        {searchFilterEl != null && searchFilterType != null && (
          <Popover
            open={searchFilterEl != null}
            anchorEl={searchFilterEl}
            onClose={closeSearchFilter}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <PopoverContentContainer
              hospitalMedicalSubjects={hospitalMedicalSubjects}
              hospitalReportedAdditionalFees={hospitalReportedAdditionalFees}
              hospitalMedicalCares={hospitalMedicalCares}
              hospitalAppealPoints={hospitalAppealPoints}
              searchFilterType={searchFilterType}
              search={search}
              close={closeSearchFilter}
            />
          </Popover>
        )}
      </Box>
    </Box>
  );
}
